import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { invokeApi } from '../lib/invokeApi';
import { useUser } from '../contexts/UserContext';
import type { TeamBillingSummary, BillingHealth, BillingInterval } from '../types/database';

interface SubscriptionState {
  subscriptionId: string | null;
  status: string | null;
  billingInterval: BillingInterval | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  hasActiveSubscription: boolean;
  billingHealth: BillingHealth;
}

export interface CheckoutResult {
  url: string;
  sessionId?: string;
  redirectType: 'checkout' | 'portal';
  promoCodeApplied: boolean;
  promoCodeEntered: boolean;
  /** Message explaining portal redirect (when user already has subscription) */
  message?: string;
  /** Existing plan ID if redirecting to portal */
  existingPlanId?: string;
}

interface UseSubscriptionReturn {
  subscription: SubscriptionState | null;
  billingSummary: TeamBillingSummary | null;
  loading: boolean;
  error: string | null;
  isTeamAdmin: boolean;
  startCheckout: (planId: string, interval: BillingInterval, promoCode?: string) => Promise<CheckoutResult>;
  openBillingPortal: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * Hook to manage Stripe subscription for a team.
 * Provides subscription state and functions to initiate checkout/portal.
 *
 * @param teamId - Optional team ID. If not provided, uses active team from UserContext.
 */
export function useSubscription(teamId?: string): UseSubscriptionReturn {
  const { activeTeam, isStaff, isSuperadmin } = useUser();
  const effectiveTeamId = teamId || activeTeam?.team_id || null;
  const isTeamAdmin = activeTeam?.is_admin || isStaff || isSuperadmin || false;

  const [billingSummary, setBillingSummary] = useState<TeamBillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch billing summary from vw_team_billing_summary
  const fetchBillingSummary = useCallback(async () => {
    if (!effectiveTeamId) {
      setBillingSummary(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: queryError } = await supabase
        .from('vw_team_billing_summary')
        .select('*')
        .eq('team_id', effectiveTeamId)
        .maybeSingle();

      if (queryError) throw queryError;

      setBillingSummary(data as TeamBillingSummary | null);
    } catch (err) {
      console.error('Failed to fetch billing summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch billing information');
    } finally {
      setLoading(false);
    }
  }, [effectiveTeamId]);

  useEffect(() => {
    fetchBillingSummary();
  }, [fetchBillingSummary]);

  // Derived subscription state from billing summary
  const subscription: SubscriptionState | null = billingSummary ? {
    subscriptionId: billingSummary.subscription_id,
    status: billingSummary.subscription_status,
    billingInterval: billingSummary.billing_interval,
    currentPeriodEnd: billingSummary.current_period_end,
    cancelAtPeriodEnd: billingSummary.cancel_at_period_end || false,
    hasActiveSubscription: billingSummary.has_active_subscription,
    billingHealth: billingSummary.billing_health
  } : null;

  /**
   * Initiate Stripe Checkout for a plan upgrade/subscription.
   * Calls the create-checkout-session edge function and returns the result.
   * Caller is responsible for redirecting to Stripe or showing warnings.
   */
  const startCheckout = useCallback(async (
    planId: string,
    interval: BillingInterval,
    promoCode?: string
  ): Promise<CheckoutResult> => {
    if (!effectiveTeamId) {
      throw new Error('No team selected');
    }

    if (!isTeamAdmin) {
      throw new Error('Only team admins can manage billing');
    }

    try {
      // Call create-checkout-session API
      const { data, error: funcError } = await invokeApi('create-checkout-session', {
        teamId: effectiveTeamId,
        planId,
        billingInterval: interval,
        promotionCode: promoCode,
      });

      if (funcError) throw funcError;
      if (!data?.url) throw new Error('No checkout URL returned');

      return {
        url: data.url,
        sessionId: data.sessionId,
        redirectType: data.redirectType ?? 'checkout',
        promoCodeApplied: data.promoCodeApplied ?? false,
        promoCodeEntered: data.promoCodeEntered ?? false,
        message: data.message,
        existingPlanId: data.existingPlanId,
      };
    } catch (err) {
      console.error('Failed to start checkout:', err);
      throw err;
    }
  }, [effectiveTeamId, isTeamAdmin]);

  /**
   * Open the Stripe Customer Portal for billing management.
   * Allows users to update payment methods, view invoices, and cancel.
   */
  const openBillingPortal = useCallback(async () => {
    if (!effectiveTeamId) {
      throw new Error('No team selected');
    }

    if (!isTeamAdmin) {
      throw new Error('Only team admins can manage billing');
    }

    try {
      // Call create-portal-session API
      const { data, error: funcError } = await invokeApi('create-portal-session', {
        teamId: effectiveTeamId,
      });

      if (funcError) throw funcError;
      if (!data?.url) throw new Error('No portal URL returned');

      // Redirect to Stripe Customer Portal
      window.location.href = data.url;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
      throw err;
    }
  }, [effectiveTeamId, isTeamAdmin]);

  return {
    subscription,
    billingSummary,
    loading,
    error,
    isTeamAdmin,
    startCheckout,
    openBillingPortal,
    refresh: fetchBillingSummary
  };
}
